import { Marked } from "marked";
import { markedHighlight } from "marked-highlight";
import hljs from "highlight.js/lib/common";
import DOMPurify from "dompurify";
import { fenceLanguage } from "./diff";
import "highlight.js/styles/github-dark.css";

/**
 * Render chat message markdown → sanitized HTML. The agent sends markdown (same as the phone renders); we
 * parse with marked (GFM, soft line breaks), syntax-highlight fenced code (and ```diff blocks) with
 * highlight.js, and sanitize with DOMPurify so decrypted content can't inject script/markup. Links open in a
 * new tab. Wide code reads well on a big screen.
 */
const marked = new Marked(
  markedHighlight({
    langPrefix: "hljs language-",
    highlight(code, lang) {
      // An agent pasting a patch rarely stops to tag the fence, so an untagged block that reads as a unified
      // diff is highlighted as one rather than dropped to plaintext.
      const tag = fenceLanguage(lang, code);
      const language = tag && hljs.getLanguage(tag) ? tag : "plaintext";
      return hljs.highlight(code, { language }).value;
    },
  }),
);
marked.setOptions({ gfm: true, breaks: true });

// Make every link open safely in a new tab.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/** Whether a link target can actually be opened by clicking it. Kept in step with the phone's rule. */
function openableLink(href: string): boolean {
  return (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    href.startsWith("#")
  );
}

/**
 * Give every listing a copy button, and take the link off anything a click can't follow.
 *
 * Done after sanitising, on a parsed document rather than by patching the HTML string: the button is markup
 * this file is adding, so it must not be handed back to the sanitiser as if it had come out of a message, and
 * a regex over `<pre>` would be guessing at markup that is already a DOM.
 *
 * The button carries no handler of its own — one delegated listener below serves every listing on the page,
 * including the ones rendered after it was installed.
 */
function withCopyButtons(html: string): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  // A markdown link may point at anything, this app's own `attachment://` references included, and a target
  // nothing can open leaves an underlined word that does nothing when clicked. The words were the useful half.
  for (const a of Array.from(doc.querySelectorAll("a"))) {
    if (!openableLink(a.getAttribute("href") ?? "")) a.replaceWith(...Array.from(a.childNodes));
  }
  for (const pre of Array.from(doc.querySelectorAll("pre"))) {
    const wrap = doc.createElement("div");
    wrap.className = "code-block";
    pre.replaceWith(wrap);
    wrap.append(pre);
    // The language the highlighter settled on, named above the listing the way the phone names it — which
    // is also how an inferred diff announces that it was read as one rather than as plain text.
    const tagged = /(?:^|\s)language-(\S+)/.exec(pre.querySelector("code")?.className ?? "")?.[1];
    const lang = fenceLanguage(tagged, pre.textContent ?? "");
    if (lang) {
      const label = doc.createElement("span");
      label.className = "code-lang";
      label.textContent = lang;
      wrap.append(label);
    }
    const button = doc.createElement("button");
    button.className = "code-copy";
    button.type = "button";
    button.textContent = "Copy";
    wrap.append(button);
  }
  return doc.body.innerHTML;
}

/**
 * One listener for every copy button on the page, present and future.
 *
 * Markdown is rendered as an HTML string in several places and injected directly, so there is nowhere to hang
 * a per-button handler; delegation from the document is what survives that. The label becomes a tick briefly
 * rather than raising a dialog — the copy either worked or it didn't, and that is the whole message.
 */
if (typeof document !== "undefined") {
  document.addEventListener("click", (e) => {
    const button = (e.target as HTMLElement | null)?.closest?.(".code-copy") as HTMLElement | null;
    const code = button?.parentElement?.querySelector("pre");
    if (!button || !code) return;
    void navigator.clipboard?.writeText(code.textContent ?? "");
    button.textContent = "Copied";
    setTimeout(() => (button.textContent = "Copy"), 1_500);
  });
}

export function renderMarkdown(text: string): string {
  const html = marked.parse(text, { async: false }) as string;
  return withCopyButtons(DOMPurify.sanitize(html));
}
